import React, { useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SimpleSearch } from './components/SimpleSearch';
import { SimpleSearchResults } from './components/SimpleSearchResults';

import { SearchForm } from './components/SearchForm';
import { SearchResults } from './components/SearchResults';
import { TestSearchButton } from './components/TestSearchButton';

import { ApiModeProvider } from './context/ApiModeContext';
import { useSearch } from './hooks/useSearch';
import type { SearchRequest } from './shared/types';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function AppContent() {
  const [isAdvancedMode, setIsAdvancedMode] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  const { 
    search, 
    searchResults, 
    searchTime, 
    isSearching,
    runTestSearch,
    testResults,
    isTestLoading 
  } = useSearch();

  const handleSearch = (request: SearchRequest) => {
    search(request);
    setShowResults(true);
    // Extract search query for display
    if (request.jobDescription.description) {
      setSearchQuery(request.jobDescription.description);
    }
  };

  const handleTestSearch = () => {
    runTestSearch();
    setShowResults(true);
    setSearchQuery('Senior Full Stack Developer');
  };

  const currentResults = testResults.length > 0 ? testResults : searchResults;
  const currentTime = testResults.length > 0 ? 0 : searchTime;
  const isLoading = isSearching || isTestLoading;

  // Simple View
  if (!isAdvancedMode) {
    return (
      <div className={`min-h-screen bg-gray-900 ${!showResults ? 'flex flex-col justify-center' : ''}`}>
        {/* <ApiModeToggle /> */}
        <SimpleSearch 
          onSearch={handleSearch}
          onAdvancedToggle={() => setIsAdvancedMode(true)}
          isLoading={isLoading}
          searchQuery={searchQuery}
          onSearchQueryChange={setSearchQuery}
        />
        
        {showResults && (
          <SimpleSearchResults
            matches={currentResults}
            searchTime={currentTime}
            isLoading={isLoading}
            query={searchQuery}
          />
        )}
      </div>
    );
  }

  // Advanced View (Original)
  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* <ApiModeToggle /> */}
      {/* Header */}
      <header className="bg-gray-800 shadow-sm border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <h1 className="text-2xl font-light tracking-wide text-white">
                student<span className="text-blue-400">search</span>
              </h1>
            </div>
            <button
              onClick={() => {
                setIsAdvancedMode(false);
              }}
              className="text-blue-400 hover:text-blue-300 text-sm"
            >
              ← Back to Simple Search
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Search Form */}
          <div className="lg:col-span-1 space-y-6">
            <SearchForm onSearch={handleSearch} isLoading={isLoading} />
            <TestSearchButton onTestSearch={handleTestSearch} isLoading={isLoading} />
          </div>

          {/* Results */}
          <div className="lg:col-span-2">
            {showResults ? (
              <SearchResults 
                matches={currentResults} 
                searchTime={currentTime}
                isLoading={isLoading}
              />
            ) : (
              <div className="bg-gray-800 rounded-lg shadow-md p-8 text-center">
                <div className="max-w-md mx-auto">
                  <h3 className="text-xl font-semibold text-white mb-2">Advanced Search Ready</h3>
                  <p className="text-gray-400 mb-6">
                    Use the detailed form to create precise searches with filters and requirements.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ApiModeProvider>
        <AppContent />
      </ApiModeProvider>
    </QueryClientProvider>
  );
}

export default App;
